import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_MONTHLY_SIGNUP_STATS } from '../../apollo/queries';
import '../css/StatsPage.css';

const SignupStatsPage = () => {
    const navigate = useNavigate();
    const [selectedYear, setSelectedYear] = useState(2025);
    const [selectedGrades, setSelectedGrades] = useState({
        all: true,
        A_GRADE: false,
        B_GRADE: false,
        C_GRADE: false,
        D_GRADE: false
    });

    // GraphQL 쿼리
    const { data: statsData, loading: statsLoading, error: statsError, refetch } = useQuery(GET_MONTHLY_SIGNUP_STATS, {
        variables: { year: selectedYear },
        fetchPolicy: 'cache-and-network'
    });

    const handleSearch = () => {
        refetch({ year: selectedYear });
    };

    const handleGradeChange = (grade) => {
        if (grade === 'all') {
            setSelectedGrades({
                all: true,
                A_GRADE: false,
                B_GRADE: false,
                C_GRADE: false,
                D_GRADE: false
            });
        } else {
            setSelectedGrades(prev => ({
                ...prev,
                all: false,
                [grade]: !prev[grade]
            }));
        }
    };

    const handleExcelDownload = () => {
        // Excel 다운로드 기능 구현
        console.log('Excel 다운로드 기능 구현 예정');
    };

    // 데이터 처리
    const processedStatsData = statsData?.monthlySignupStats || [];
    const totalSignups = processedStatsData.reduce((sum, stat) => sum + stat.totalSignups, 0);
    const maxSignups = Math.max(...processedStatsData.map(stat => stat.totalSignups), 1);

    // 필터링된 데이터
    const filteredData = processedStatsData.map(data => {
        let total = data.totalSignups;
        let a = data.aGradeCount;
        let b = data.bGradeCount;
        let c = data.cGradeCount;
        let d = data.dGradeCount;

        // 등급별 필터링
        if (!selectedGrades.all) {
            a = selectedGrades.A_GRADE ? a : 0;
            b = selectedGrades.B_GRADE ? b : 0;
            c = selectedGrades.C_GRADE ? c : 0;
            d = selectedGrades.D_GRADE ? d : 0;
            total = a + b + c + d;
        }

        const progressWidth = maxSignups > 0 ? (total / maxSignups) * 100 : 0;

        return {
            ...data,
            displayTotal: total,
            displayA: a,
            displayB: b,
            displayC: c,
            displayD: d,
            progressWidth: Math.min(progressWidth, 100)
        };
    });

    if (statsLoading) {
        return (
            <div className="admin-main-content">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>가입 통계를 불러오는 중...</p>
                </div>
            </div>
        );
    }

    if (statsError) {
        return (
            <div className="admin-main-content">
                <div className="error-container">
                    <p>가입 통계를 불러오는 중 오류가 발생했습니다.</p>
                    <button onClick={() => refetch()}>다시 시도</button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 통계분석 관리 &gt; 가입 통계</span>
            </div>
            <div className="admin-main-content">
                <aside className="admin-sidebar">
                    <div className="sidebar-menu">
                        <h3>통계분석 관리</h3>
                        <ul>
                            <li onClick={() => navigate('/admin/stats/connection')}>접속 통계</li>
                            <li onClick={() => navigate('/admin/stats/quotation')}>견적 통계</li>
                            <li className="active">가입 통계</li>
                        </ul>
                    </div>
                </aside>
                <main className="admin-content-stats">
                    <div className="page-header">
                        <div className="page-title-bar"></div>
                        <h2>가입 통계</h2>
                    </div>

                    <div className="stats-search-filters">
                        <div className="filter-row">
                            <label>년도 간 검색</label>
                            <select 
                                className="filter-select"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            >
                                <option value={2025}>2025</option>
                                <option value={2024}>2024</option>
                                <option value={2023}>2023</option>
                            </select>
                        </div>
                        <div className="filter-row">
                            <label>주간 검색</label>
                            <input type="text" className="filter-input-date" defaultValue="2025 1월 1주" />
                        </div>
                        <div className="filter-row">
                            <label>레벨 검색</label>
                            <div className="filter-checkboxes">
                                <label>
                                    <input 
                                        type="checkbox" 
                                        checked={selectedGrades.all}
                                        onChange={() => handleGradeChange('all')}
                                    /> 전체
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        checked={selectedGrades.A_GRADE}
                                        onChange={() => handleGradeChange('A_GRADE')}
                                    /> A급
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        checked={selectedGrades.B_GRADE}
                                        onChange={() => handleGradeChange('B_GRADE')}
                                    /> B급
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        checked={selectedGrades.C_GRADE}
                                        onChange={() => handleGradeChange('C_GRADE')}
                                    /> C급
                                </label>
                                <label>
                                    <input 
                                        type="checkbox" 
                                        checked={selectedGrades.D_GRADE}
                                        onChange={() => handleGradeChange('D_GRADE')}
                                    /> D급
                                </label>
                            </div>
                        </div>
                    </div>
                    <button className="search-button-stats" onClick={handleSearch}>검 색</button>

                    <div className="stats-data-container">
                        <div className="stats-data-header">
                            <span>
                                현재 가입자 수 : {totalSignups.toLocaleString()} 건
                            </span>
                            <button className="btn-excel-download" onClick={handleExcelDownload}>
                                엑셀 저장
                            </button>
                        </div>
                        <table className="stats-table">
                            <thead>
                                <tr>
                                    <th>날짜</th>
                                    <th>그래프</th>
                                    <th>비율%</th>
                                    <th>전체</th>
                                    <th>A급</th>
                                    <th>B급</th>
                                    <th>C급</th>
                                    <th>D급</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((data) => (
                                    <tr key={data.month}>
                                        <td data-label="날짜">{data.month}</td>
                                        <td data-label="그래프" className="progress-bar-cell">
                                            <div className="progress-bar-container">
                                                <div 
                                                    className="progress-bar" 
                                                    style={{ width: `${data.progressWidth}%` }}
                                                ></div>
                                            </div>
                                        </td>
                                        <td data-label="비율%">{data.percentage}%</td>
                                        <td data-label="전체">{data.displayTotal}</td>
                                        <td data-label="A급">{data.displayA}</td>
                                        <td data-label="B급">{data.displayB}</td>
                                        <td data-label="C급">{data.displayC}</td>
                                        <td data-label="D급">{data.displayD}</td>
                                    </tr>
                                ))}
                                {filteredData.length === 0 && (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                                            해당 년도의 가입 통계 데이터가 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </main>
            </div>
        </>
    );
};

export default SignupStatsPage; 